import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { notificationsApi, type StaffNotification } from '@/lib/api/notifications.js';
import { useAuthStore } from '@/stores/auth.js';
import { formatRelativeTime } from '@/lib/format.js';
import { toastInfo } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';
import { createReconnectingWebSocket } from '@/lib/ws-reconnect.js';

export function NotificationBell() {
  const userId = useAuthStore((s) => s.user?.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const unreadQuery = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: () => notificationsApi.unreadCount(),
    enabled: Boolean(userId),
  });

  const listQuery = useQuery({
    queryKey: ['notifications-list'],
    queryFn: () => notificationsApi.list({ pageSize: 20 }),
    enabled: open && Boolean(userId),
  });

  const unread = unreadQuery.data?.count ?? 0;
  const badge = unread > 99 ? '99+' : unread > 0 ? String(unread) : null;

  useEffect(() => {
    const base = unread > 0 ? `(${unread > 99 ? '99+' : unread}) Repairiz` : 'Repairiz';
    document.title = base;
  }, [unread]);

  useEffect(() => {
    if (!userId) return;
    const socket = createReconnectingWebSocket({
      path: `/ws/user/${userId}`,
      onMessage: (event) => {
        try {
          const msg = JSON.parse(event.data as string) as {
            type?: string;
            payload?: StaffNotification;
          };
          if (msg.type !== 'notification.new' || !msg.payload) return;
          void queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
          void queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
          toastInfo(msg.payload.title, {
            duration: 5000,
          });
        } catch {
          /* ignore */
        }
      },
    });
    return () => socket.close();
  }, [userId, queryClient]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const markAllMut = useMutation({
    mutationFn: () => notificationsApi.markRead({ all: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
    },
  });

  async function openNotification(n: StaffNotification) {
    if (!n.readAt) {
      await notificationsApi.markRead({ notificationIds: [n.id] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-50"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {badge ? (
          <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-red-600 px-1 text-center text-[10px] font-bold leading-4 text-white">
            {badge}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-[22rem] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl sm:w-96">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <h2 className="font-bold text-slate-950">Notifications</h2>
            <button
              type="button"
              className="text-xs font-semibold text-primary disabled:opacity-50"
              disabled={markAllMut.isPending || unread === 0}
              onClick={() => markAllMut.mutate()}
            >
              Mark all ✓
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {(listQuery.data?.data ?? []).map((n) => {
              const unreadRow = !n.readAt;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => void openNotification(n)}
                  className={cn(
                    'block w-full border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50',
                    unreadRow ? 'border-l-4 border-l-primary bg-blue-50/40' : 'border-l-4 border-l-transparent',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">
                      {unreadRow ? '🔵 ' : ''}
                      {n.title}
                    </p>
                    <span className="shrink-0 text-[10px] text-slate-400">
                      {formatRelativeTime(n.createdAt)}
                    </span>
                  </div>
                  {n.body ? (
                    <p className="mt-1 line-clamp-2 text-xs text-slate-600">{n.body}</p>
                  ) : null}
                  {n.link ? (
                    <span className="mt-2 inline-block text-xs font-semibold text-primary">
                      → Open
                    </span>
                  ) : null}
                </button>
              );
            })}
            {!listQuery.isLoading && (listQuery.data?.data ?? []).length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-500">No notifications yet.</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
